import React, { useEffect, useCallback, useContext } from 'react';
import './calculator.scss';
import {
  number,
  oneOfType,
  arrayOf,
  string,
  func,
  bool
} from 'prop-types';
import classNames from 'classnames';
import { Alert, Button } from '@thd-olt-component-react/core-ui';
import { ExperienceContext } from '~/@thd-nucleus/experience-context';
import { measuringTipsClickAnalytics, track } from './subcomponents/Analytics/analytics';
import { CalculatorOverlay } from './subcomponents/CalculatorOverlay/CalculatorOverlay';
import { CalculatorAreaElement } from './subcomponents/CalculatorAreaElement';
import { CalculatorWidthHeightElement } from './subcomponents/CalculatorWidthHeightElement';
import { useCalculator } from './useCalculator';
import { MulchOptions } from './subcomponents/MulchOptions/MulchOptions';

const Calculator = ({
  unitsPerCase,
  addAdditional,
  calculatorType,
  maxAreaNumber,
  lineItemName,
  dimensionNames,
  productCategory,
  onCalculate,
  hideHeader,
  hideMeasuringTips,
  showMulchOptions,
  showDescriptions,
  hideAreaText,
  descriptionText
}) => {
  const MIN_AREA_NUMBER = 1;
  const {
    channel
  } = useContext(ExperienceContext);
  const [calculate,
    { setCalculate, addElement, calculateTotal }
  ] = useCalculator({ showMulchOptions, unitsPerCase, addAdditional, calculatorType });

  useEffect(() => {
    if (calculate.isClicked) {
      track();
    }
  }, [calculate]);

  const bindCalcTotal = useCallback(() => {
    const result = calculateTotal() || {};
    if (onCalculate) {
      onCalculate({
        mulchType: result.mulchType,
        bagSize: result.bagSize,
        quantity: result.quantity,
        bulkQuantity: result.bulkQuantity,
        coverage: result.coverage,
        error: result.hasError
      });
    }
  }, [calculate]);

  const handleBagSize = useCallback((event) => {
    const { value = '' } = event.target;
    setCalculate({
      ...calculate,
      bagSize: value
    });
  }, [calculate]);

  const handleMulchType = useCallback((event) => {
    let mulchType = '';
    let bagSize = '';
    const { value = '' } = event.target;

    if (value === 'wood' || value === 'top soil') {
      mulchType = value;
      bagSize = '1.5';
    } else {
      mulchType = value;
      bagSize = '0.8';
    }
    setCalculate({
      ...calculate,
      mulchType,
      bagSize
    });
  }, [calculate]);

  // TODO: Use objects / refs instead of arrays to keep track of these values. useReducer
  const handleChangeWidthHeight = useCallback((even, index, height) => {
    const { valueDescription, valueWidths, valueHeights, valueDepths } = calculate;
    const re = /^[0-9\b]{0,5}$/;
    const { value = '', name = '' } = even.target;
    if (value === '' || re.test(value) || name === 'description') {
      if (name === 'depth') {
        valueDepths[index] = value;
      } else if (name === 'description') {
        valueDescription[index] = value;
      } else if (height) {
        valueHeights[index] = value;
      } else {
        valueWidths[index] = value;
      }
      setCalculate({
        ...calculate,
        value,
        valueDescription,
        valueWidths,
        valueHeights,
        valueDepths
      });
    }
  }, [calculate]);

  const handleChange = useCallback((event, index) => {
    const { valueDescription, valueAreas, valueDepths } = calculate;
    const re = /^[0-9\b]{0,5}$/;
    const { value = '', name = '' } = event.target;
    if (value === '' || re.test(value) || name === 'description') {
      if (name === 'depth') {
        valueDepths[index] = value;
      } else if (name === 'description') {
        valueDescription[index] = value;
      } else {
        valueAreas[index] = { value, changed: true };
      }
      setCalculate({
        ...calculate,
        value,
        valueDescription,
        valueAreas,
        valueDepths
      });
    }
  }, [calculate]);

  const hideOverlay = useCallback(() => {
    setCalculate({
      ...calculate,
      overlayOpened: false
    });
    // this seems like a hack?
    document.body.style.overflow = 'auto';
  }, [calculate]);

  const onTipsClick = useCallback(() => {
    const payload = {
      eventName: 'calculator tips',
      primaryCategory: 'overlay'
    };
    measuringTipsClickAnalytics(payload);
    setCalculate({
      ...calculate,
      overlayOpened: true
    });
    if (typeof window !== 'undefined') {
      window.history.pushState({ page: 'TIPSOVERLAY' }, null, '');
    }
  }, [calculate]);

  const removeWidthHeightElement = useCallback((index) => {
    const { step } = calculate;
    let errorCal = false;
    if (step > MIN_AREA_NUMBER) {
      setCalculate((prevState) => {
        const {
          hasError,
          valueDescription,
          mulchType,
          valueHeights,
          valueWidths,
          valueDepths
        } = prevState;
        valueDescription.splice(index, 1);
        valueHeights.splice(index, 1);
        valueWidths.splice(index, 1);
        valueDepths.splice(index, 1);
        if (valueHeights.length === valueWidths.length) {
          for (let i = 0; i < valueHeights.length; i += 1) {
            errorCal = errorCal || valueHeights[i] === '' || valueWidths[i] === '';
          }
        }
        return {
          ...calculate,
          hasError: (hasError && errorCal),
          step: valueHeights.length,
          valueDescription,
          mulchType,
          valueHeights,
          valueWidths,
          valueDepths
        };
      });
    }
  }, [calculate]);

  const removeAreaElement = useCallback((index) => {
    const { step } = calculate;
    let errorCal = false;
    if (step > MIN_AREA_NUMBER) {
      setCalculate((prevState) => {
        const {
          valueDescription,
          mulchType,
          valueAreas,
          valueDepths
        } = prevState;
        valueDescription.splice(index, 1);
        valueAreas.splice(index, 1);
        valueDepths.splice(index, 1);
        for (let i = 0; i < valueAreas.length; i += 1) {
          errorCal = errorCal || valueAreas[i].value === '';
        }
        return {
          ...calculate,
          hasError: errorCal,
          step: valueAreas.length,
          valueDescription,
          mulchType,
          valueAreas,
          valueDepths
        };
      });
    }
  }, [calculate]);

  const handleToggleText = useCallback(() => {
    setCalculate((prevState) => {
      const {
        calcByArea,
        calcByWidthHeight,
        valueAreas,
        valueDepths,
        valueDescription,
        valueHeights,
        valueWidths
      } = prevState;
      const areaFieldCount = valueAreas.length;
      const widthFieldCount = valueWidths.length;
      const additionalRowsNeeded = calcByArea && areaFieldCount !== widthFieldCount
        ? areaFieldCount - widthFieldCount
        : 0;
      // clear WxH when switching back from area if it's been changed
      const updatedHeights = calcByArea
        ? valueHeights.map((height, heightIndex) => {
          return valueAreas[heightIndex].changed ? '' : height;
        })
        : valueHeights;
      const updatedWidths = calcByArea
        ? valueWidths.map((width, widthIndex) => {
          return valueAreas[widthIndex].changed ? '' : width;
        })
        : valueWidths;
      const newValueHeights = additionalRowsNeeded
        ? updatedHeights.concat(new Array(additionalRowsNeeded).fill(''))
        : updatedHeights;
      const newValueWidths = additionalRowsNeeded
        ? updatedWidths.concat(new Array(additionalRowsNeeded).fill(''))
        : updatedWidths;
      const newValueAreas = calcByWidthHeight
        ? updatedHeights.map((width, widthIndex) => {
          if (!!updatedHeights[widthIndex] || !valueAreas[widthIndex]) {
            return { value: `${width * (updatedHeights[widthIndex] || 0)}`, changed: false };
          }
          return valueAreas[widthIndex];
        })
        : valueAreas;
      const newToggle = calcByArea ? 'Calculate by Square Footage' : `Calculate by ${dimensionNames.join(' x ')}`;
      return {
        ...calculate,
        calcByArea: !calcByArea,
        calcByWidthHeight: !calcByWidthHeight,
        hasError: false,
        step: 1,
        valueDescription: [''],
        valueWidths: [''],
        valueHeights: [''],
        valueAreas: [{ value: '', changed: false }],
        valueDepths,
        toggleText: newToggle
      };
    });
  }, [calculate]);

  const tipsClass = classNames({
    calculator__tips: channel === 'mobile',
  });
  const prompt = 'How much will you need?';
  const description = 'Please note: calculations are estimates and can only be made using whole numbers.';
  let errorBoxWidth = false;
  let errorBoxHeight = false;
  let errorBox = false;
  const { toggleText } = calculate;
  return (
    <div className="calculator">
      {!hideHeader && (
        <h2 className="calculator__header">
          <img
            className="calculator__calculator-icon"
            src="https://assets.homedepot-static.com/images/v1/plcc-calculator.svg"
            alt="calc icon"
            width="20"
            height="25"

          />
          {prompt}
        </h2>
      )}
      <div>
        {!descriptionText ? description : descriptionText}
      </div>
      {showMulchOptions && (
        <MulchOptions
          channel={channel}
          size={calculate.bagSize}
          mulch={calculate.mulchType}
          handleBagSize={handleBagSize}
          handleMulchType={handleMulchType}
        />
      )}
      <div className="calculator__measuring">
        <button type="submit" className="calculator__measuring__toggle" onClick={() => handleToggleText()}>
          {toggleText}
        </button>
        {/* TODO: Need To figure out when to load Measuring Tips overlay. */}
        {lineItemName === 'Area' && !hideMeasuringTips && (
          <button type="button" className={tipsClass} onClick={onTipsClick}>
            <span>
              Measuring Tips
            </span>
            <span className="calculator__measuring__tips-info-icon" />
          </button>
        )}
      </div>

      {calculate.calcByWidthHeight
        && calculate.valueWidths.map((element, elementIndex) => {
          errorBoxWidth = false;
          errorBoxHeight = false;
          if (calculate.hasError) {
            errorBoxWidth = calculate.valueWidths[elementIndex] === '';
            errorBoxHeight = calculate.valueHeights[elementIndex] === '';
          }
          return (
            <CalculatorWidthHeightElement
              index={elementIndex}
              key={elementIndex}
              errorBoxWidth={errorBoxWidth}
              errorBoxHeight={errorBoxHeight}
              valueDescription={calculate.valueDescription}
              valueHeights={calculate.valueHeights}
              valueWidths={calculate.valueWidths}
              valueDepths={calculate.valueDepths}
              step={calculate.step}
              channel={channel}
              calculatorType={calculatorType}
              lineItemName={lineItemName}
              dimensionNames={dimensionNames}
              handleChangeWidthHeight={handleChangeWidthHeight}
              removeWidthHeightElement={removeWidthHeightElement}
              showMulchOptions={showMulchOptions}
              showDescriptions={showDescriptions}
              hideAreaText={hideAreaText}
            />
          );
        })}
      {calculate.calcByArea
        && calculate.valueAreas.map((element, elementIndex) => {
          errorBox = false;
          if (calculate.hasError) {
            errorBox = calculate?.valueAreas[elementIndex].value === '';
          }
          return (
            <CalculatorAreaElement
              key={elementIndex}
              index={elementIndex}
              valueDescription={calculate.valueDescription}
              valueAreas={calculate.valueAreas}
              valueDepths={calculate.valueDepths}
              calculatorType={calculatorType}
              lineItemName={lineItemName}
              step={calculate.step}
              errorBox={errorBox}
              channel={channel}
              handleChange={handleChange}
              removeAreaElement={removeAreaElement}
              showMulchOptions={showMulchOptions}
              showDescriptions={showDescriptions}
              hideAreaText={hideAreaText}
            />
          );
        })}
      {calculate.step < maxAreaNumber
        && (
          <button type="button" className="calculator__add-area" onClick={() => addElement()}>
            {`+ Add ${lineItemName}`}
          </button>
        )}

      {calculate.hasError
        && (
          <div>
            <Alert type="danger" message="Please enter all required fields." inline />
          </div>
        )}
      <Button
        id="calculate-button"
        className="calculator__inputs-calculate"
        outline
        dark
        onClick={bindCalcTotal}
      >
        Calculate
      </Button>

      {showMulchOptions && (
        <div className="calculator__alternate__description">
          {description}
        </div>
      )}

      {calculate.overlayOpened && (
        <CalculatorOverlay
          handleClose={() => { hideOverlay(); }}
          overlayOpened={calculate.overlayOpened}
          channel={channel}
        />
      )}
    </div>
  );
};

Calculator.displayName = 'Calculator';

Calculator.dataModel = {};

Calculator.propTypes = {
  unitsPerCase: oneOfType([string, number]),
  calculatorType: string,
  maxAreaNumber: number,
  lineItemName: string,
  dimensionNames: arrayOf(string),
  productCategory: string,
  addAdditional: bool,
  onCalculate: func,
  hideHeader: bool,
  hideMeasuringTips: bool,
  showMulchOptions: bool,
  showDescriptions: bool,
  hideAreaText: bool,
  descriptionText: string
};
Calculator.defaultProps = {
  unitsPerCase: '0',
  calculatorType: '',
  lineItemName: '',
  dimensionNames: ['Width', 'Height'],
  productCategory: '',
  addAdditional: false,
  maxAreaNumber: 3,
  onCalculate: () => { },
  hideHeader: false,
  hideMeasuringTips: false,
  showMulchOptions: false,
  showDescriptions: false,
  hideAreaText: false,
  descriptionText: ''
};
export { Calculator };
